import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Typography } from '@mui/material';
import { listIntakeTransfers } from '../../api/intakeTransfers';
import { useAuth } from '../../context/authContext';
import { IntakeTransferRequest } from '../../types/intakeTransfer';
import { ApproveTransferDialog, RejectTransferDialog } from './TransferDecisionDialogs';
import { TransferRequestCard } from './TransferRequestCard';
import { SUPER_ADMIN_ROLE } from './transferUtils';

/** The student's own transfer requests, on their Academic tab. Hidden when
 * there are none. `highlightId` comes from a notification link. */
export const StudentTransferRequests = ({
  studentId,
  highlightId,
}: {
  studentId: number;
  highlightId?: number | null;
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role.name === SUPER_ADMIN_ROLE;
  const [approving, setApproving] = useState<IntakeTransferRequest | null>(null);
  const [rejecting, setRejecting] = useState<IntakeTransferRequest | null>(null);
  const highlightedRef = useRef<HTMLDivElement | null>(null);

  const query = useQuery({
    queryKey: ['intake-transfers', 'student', studentId],
    queryFn: () => listIntakeTransfers({ page: 1, pageSize: 50, studentId }),
  });

  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [query.data, highlightId]);

  const requests = query.data?.data ?? [];
  if (requests.length === 0) return null;

  return (
    <>
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Intake / batch transfer requests
      </Typography>
      <Stack spacing={1.5}>
        {requests.map((r) => (
          <TransferRequestCard
            key={r.id}
            ref={r.id === highlightId ? highlightedRef : undefined}
            request={r}
            highlighted={r.id === highlightId}
            onApprove={isSuperAdmin && r.status === 'PENDING' ? () => setApproving(r) : undefined}
            onReject={isSuperAdmin && r.status === 'PENDING' ? () => setRejecting(r) : undefined}
          />
        ))}
      </Stack>
      <ApproveTransferDialog request={approving} onClose={() => setApproving(null)} />
      <RejectTransferDialog request={rejecting} onClose={() => setRejecting(null)} />
    </>
  );
};
